/* eslint-disable react/no-array-index-key */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
  Slide,
  IconButton,
  FormControl,
  Box,
  TextField,
  FormHelperText
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import AddIcon from '@mui/icons-material/Add';
import {
  CLEAR_CLIENT_OPTIONS,
  LOAD_CLIENTS,
  loadAllClients
} from 'src/store/actions/clientActions';
import { selectClientsList } from 'src/store/selectors/clientSelectors';
import { isLoadingSelector } from 'src/store/selectors/statusSelector';
import { LOAD_PENDING_PAYMENT_INCOME_CFDI } from 'src/store/constants/CFDI';
import { loadPendingPaymentIncomeCFDIsForClient } from 'src/store/actions/cfdiActions';
import { SEND_CREDIT_CARD_PAYMENT_LINK } from 'src/store/actions/cardMovementsActions';
import Autocomplete from 'src/components/Autocomplete/Autocomplete';
import Preloader from 'src/components/Preloader';
import { formatAmountWithDecimals } from 'src/utils/formatAmout';
import MoneyMask from 'src/components/MoneyMask/MoneyMask';
import makeInitialState from './initialState';
import validationSchema from './schema';
import useStyles from './styles';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const CreditCardPaymentForm = ({ open, handleClose, onSubmit }) => {
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const [invoiceList, setInvoiceList] = useState([]);
  const [invoicesCount, setInvoicesCount] = useState(1);
  const loading = useSelector(
    isLoadingSelector([SEND_CREDIT_CARD_PAYMENT_LINK.action])
  );
  const isLoadingInvoices = useSelector(
    isLoadingSelector([LOAD_PENDING_PAYMENT_INCOME_CFDI.action])
  );
  const loadingClients = useSelector(isLoadingSelector([LOAD_CLIENTS.action]));
  const clientList = useSelector(selectClientsList);

  const initialState = makeInitialState();
  const resetRef = useRef();
  const { t } = useTranslation();

  const incrementInputCount = (setFieldValue, values) => {
    return () => {
      if (invoicesCount < invoiceList.length) {
        setFieldValue('invoices', [...values.invoices, null]);
      }
      setInvoicesCount(preValue => preValue + 1);
    };
  };

  useEffect(() => {
    if (open) {
      dispatch({ type: CLEAR_CLIENT_OPTIONS });
      dispatch(loadAllClients());
    }
    return () => {
      if (resetRef.current) {
        resetRef.current();
      }
      setInvoicesCount(1);
      setInvoiceList([]);
    };
  }, [dispatch, open]);

  const handleClientChange = useCallback(
    (setFieldValue, value) => {
      setFieldValue('clientId', value?.id ?? '');
      setFieldValue('invoices', [null]);
      setInvoicesCount(1);
      setInvoiceList([]);
      if (value?.id) {
        dispatch(
          loadPendingPaymentIncomeCFDIsForClient(value.id, setInvoiceList)
        );
      }
    },
    [dispatch]
  );

  const handleSubmitForm = useCallback(
    (values, { resetForm }) => {
      onSubmit(values, handleClose);
      resetForm(initialState);
    },
    [onSubmit, handleClose, initialState]
  );

  const handleCloseDialog = () => {
    setInvoiceList([]);
    handleClose();
  };

  const createHandleRemoveInvoice = (index, setFieldValue, values) => () => {
    const updatedInvoices = values.invoices.filter((_, i) => i !== index);
    setFieldValue('invoices', updatedInvoices);
    setInvoicesCount(preValue => preValue - 1);
  };

  const getInvoiceOptionLabel = option =>
    option
      ? `${t('COMMON.CONTRACT')} ${option.contract.folio} - ${t(
          'COMMON.SERIE'
        )} ${option.series} - ${option.folio} - $${formatAmountWithDecimals(
          option?.amountIvaIncluded
        )}`
      : '';

  return (
    <Dialog
      disableBackdropClick
      disableEscapeKeyDown
      classes={{ paper: classes.root }}
      open={open}
      TransitionComponent={Transition}
    >
      <DialogTitle className={classes.header} id="form-general-dialog-title">
        <IconButton
          key="close"
          aria-label="Close"
          color="inherit"
          onClick={handleCloseDialog}
          className={classes.closeIcon}
        >
          <CloseIcon />
        </IconButton>
        <Typography className={classes.header} color="primary" variant="h2">
          {t('CARD_MOVEMENTS.GENERATE_ONE_TIME_PAYMENT')}
        </Typography>
      </DialogTitle>
      <DialogContent style={{ marginBottom: '16px' }}>
        {loading ? (
          <Preloader />
        ) : (
          <Formik
            initialValues={initialState}
            validationSchema={validationSchema}
            onSubmit={handleSubmitForm}
            enableReinitialize
          >
            {({
              errors,
              handleBlur,
              handleSubmit,
              touched,
              values,
              resetForm,
              setFieldValue
            }) => {
              resetRef.current = () => resetForm();
              const invoicesErrors = errors.invoices || [];
              return (
                <form onSubmit={handleSubmit}>
                  <FormControl fullWidth sx={{ mt: 2 }}>
                    <Autocomplete
                      options={clientList}
                      id="clientId"
                      label={t('COMMON.CLIENT')}
                      isLoading={loadingClients}
                      getOptionLabel={option => {
                        return option.fullName
                          ? `${option.fullName} - ID:${option.id}`
                          : '';
                      }}
                      value={clientList.find(
                        client => client.id === values.clientId
                      )}
                      onChange={(_e, value) => {
                        handleClientChange(setFieldValue, value);
                      }}
                      error={Boolean(touched.clientId && errors.clientId)}
                      helperText={touched.clientId && t(errors.clientId)}
                    />
                  </FormControl>

                  {values.clientId && (
                    <>
                      <FormControl fullWidth sx={{ mt: 2 }}>
                        {Array(invoicesCount)
                          .fill('')
                          .map((_, index) => (
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                justifyContent: 'space-between',
                                flexDirection: 'column',
                                marginTop: index === 0 ? '16px' : '0px'
                              }}
                              key={`box-${index}`}
                            >
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  width: '100%'
                                }}
                              >
                                <Autocomplete
                                  key={`invoice-${index}`}
                                  options={invoiceList.filter(
                                    invoice =>
                                      !values.invoices.some(selectedInvoice => {
                                        if (selectedInvoice) {
                                          return (
                                            selectedInvoice.id === invoice.id
                                          );
                                        }
                                        return false;
                                      })
                                  )}
                                  id="invoices"
                                  label={t('COMMON.CONTRACTS')}
                                  value={values.invoices[index] ?? null}
                                  loading={isLoadingInvoices}
                                  disabled={isLoadingInvoices}
                                  loadingText={t('COMMON.LOADING')}
                                  getOptionLabel={getInvoiceOptionLabel}
                                  onChange={(_e, value) => {
                                    const updatedInvoices = [
                                      ...values.invoices
                                    ];
                                    if (value) {
                                      updatedInvoices[index] = value
                                        ? {
                                            ...value,
                                            amountToPay:
                                              value?.amountIvaIncluded
                                          }
                                        : null;
                                    }
                                    setFieldValue('invoices', updatedInvoices);
                                  }}
                                  error={Boolean(
                                    touched.invoices && invoicesErrors[index]
                                  )}
                                  sx={{
                                    width: '60%'
                                  }}
                                />

                                <TextField
                                  sx={{
                                    width: '35%'
                                  }}
                                  InputProps={{
                                    autoComplete: 'off',
                                    inputComponent: MoneyMask,
                                  }}
        InputLabelProps={{ shrink: true }}
                                  key={`amount-${index}`}
                                  error={Boolean(
                                    touched.invoices && invoicesErrors[index]
                                  )}
                                  disabled={!values.invoices[index]}
                                  label={t('COMMON.AMOUNT_WITH_IVA')}
                                  onBlur={handleBlur}
                                  name={`invoices[${index}].amountToPay`}
                                  onChange={e => {
                                    const newValues = [...values.invoices];
                                    if (
                                      values.invoices[index]?.amountToPay ||
                                      values.invoices[index]?.amountToPay === 0
                                    ) {
                                      if (
                                        e.target.value ||
                                        e.target.value === 0
                                      ) {
                                        newValues[index] = {
                                          ...newValues[index],
                                          amountToPay: e.target.value
                                        };
                                      }
                                    }

                                    setFieldValue('invoices', newValues);
                                  }}
                                  value={
                                    values.invoices[index]?.amountToPay ?? ''
                                  }
                                  variant="outlined"
                                />
                                <IconButton
                                  sx={{
                                    width: '20px',
                                    height: '20px'
                                  }}
                                  aria-label="delete"
                                  key={`delete-${index}`}
                                  disabled={invoicesCount === 1}
                                  onClick={createHandleRemoveInvoice(
                                    index,
                                    setFieldValue,
                                    values
                                  )}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Box>

                              <FormHelperText
                                error
                                sx={{
                                  textAlign: 'left',
                                  alignSelf: 'flex-start',
                                  width: '100%',
                                  pl: 0,
                                  ml: 0
                                }}
                              >
                                {touched.invoices && invoicesErrors[index]
                                  ? t(invoicesErrors[index])
                                  : ' '}
                              </FormHelperText>
                            </Box>
                          ))}
                      </FormControl>

                      <Box
                        key="addButton"
                        sx={{
                          display: 'flex',
                          alignItems: 'center'
                        }}
                      >
                        <Button
                          type="button"
                          key="addButton"
                          color="primary"
                          variant="text"
                          disabled={
                            isLoadingInvoices ||
                            invoicesCount >= invoiceList.length
                          }
                          onClick={incrementInputCount(setFieldValue, values)}
                          startIcon={<AddIcon />}
                        >
                          {t('COMMON.ADD')}
                        </Button>
                      </Box>

                      <TextField
                        fullWidth
                        margin="normal"
                        disabled
                        label={t('COMMON.TOTAL_AMOUNT')}
                        InputProps={{
                          readOnly: true,
                          inputComponent: MoneyMask,
                        }}
        InputLabelProps={{ shrink: true }}
                        value={formatAmountWithDecimals(
                          values.invoices.reduce((acc, invoice) => {
                            if (invoice?.amountToPay) {
                              return acc + invoice.amountToPay;
                            }
                            return acc;
                          }, 0)
                        )}
                      />
                    </>
                  )}

                  <Box sx={{ mt: 3 }}>
                    <Button
                      className={
                        isLoadingInvoices ||
                        !values.invoices.length ||
                        !values.clientId
                          ? classes.buttons
                          : clsx(classes.label, classes.buttons)
                      }
                      type="submit"
                      color="primary"
                      variant="outlined"
                      disabled={
                        loading ||
                        isLoadingInvoices ||
                        !values.invoices.length ||
                        !values.clientId
                      }
                    >
                      {t('CARD_MOVEMENTS.SEND_PAYMENT_LINK')}
                    </Button>
                  </Box>
                </form>
              );
            }}
          </Formik>
        )}
      </DialogContent>
    </Dialog>
  );
};

CreditCardPaymentForm.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired
};

export default CreditCardPaymentForm;
